import { useEffect, useState } from 'react';
import { fetchLabelCounts } from '@/features/datasets/utils/processAnalysis';
import { knownLabelsForAiGeneration } from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import { TaskType } from '@/shared/domain/taskTypes';

/** Must match fallback-select copy and optune-utils ``NO_PREDICTIONS_GENERATION_TARGET``. */
export const NO_PREDICTIONS_GENERATION_TARGET = 'No predictions';

const countsToTargets = (counts) =>
    Object.entries(counts || {}).map(([name, info]) => ({
        name,
        total: typeof info === 'number' ? info : (info?.total ?? 0),
    }));

/** Catalog order first, then any extra counted names; fill missing totals with 0. */
export const mergeGenerationTargets = (counted, catalogNames) => {
    const byName = new Map((counted || []).map((target) => [target.name, target]));
    const ordered = [];
    const seen = new Set();
    (catalogNames || []).forEach((name) => {
        if (!name || seen.has(name)) return;
        seen.add(name);
        ordered.push(byName.get(name) || { name, total: 0 });
    });
    (counted || []).forEach((target) => {
        if (!target?.name || seen.has(target.name)) return;
        seen.add(target.name);
        ordered.push(target);
    });
    return ordered;
};

/**
 * Multi-label tasks whose fallback is "No predictions" (no named
 * ``rejection_label_name``) also generate unlabeled none-of-the-labels samples.
 */
export const shouldAppendNoPredictionsGenerationTarget = (usecase, catalogNames) => {
    if (usecase?.task_type !== TaskType.classification || !usecase?.is_multilabel) {
        return false;
    }
    if (usecase.rejection_label_name) {
        return false;
    }
    return !(catalogNames || []).includes(NO_PREDICTIONS_GENERATION_TARGET);
};

/** Real class names only — "No predictions" is a virtual target, not a selectable label. */
export const catalogLabelNames = (names) =>
    (names || []).filter((name) => name !== NO_PREDICTIONS_GENERATION_TARGET);

const catalogNamesForUsecase = (usecase) => {
    const catalogNames = knownLabelsForAiGeneration({ usecase, taskType: usecase?.task_type });
    if (shouldAppendNoPredictionsGenerationTarget(usecase, catalogNames)) {
        return [...catalogNames, NO_PREDICTIONS_GENERATION_TARGET];
    }
    return catalogNames;
};

/**
 * Labels (classification) or entity types (NER) with their dataset record
 * counts, for the "Generate data with AI" modal banner, per-target count
 * inputs, and entity tags. Prefers the counts already denormalized on the
 * usecase (`label_names_count` / `entity_names_count`), falling back to
 * `POST get-label-counts` when the usecase carries names without counts.
 * New tasks often have `manually_added_labels` / `manually_added_entities`
 * before any count map exists — those names still become targets so the
 * per-label configuration sections can render.
 */
export default function useGenerationTargets(usecase, enabled) {
    const isNer = usecase?.task_type === TaskType.NER;
    const usecaseCounts = isNer ? usecase?.entity_names_count : usecase?.label_names_count;
    const [fetched, setFetched] = useState(null);

    const fromUsecase = countsToTargets(usecaseCounts);
    const catalogNames = catalogNamesForUsecase(usecase);
    const needsFetch = enabled && !!usecase?._id && fromUsecase.length === 0;

    useEffect(() => {
        if (!needsFetch) return undefined;
        let cancelled = false;
        fetchLabelCounts({ usecaseId: usecase._id }).then((res) => {
            if (cancelled) return;
            setFetched(countsToTargets(isNer ? res.entity_counts : res.label_counts));
        });
        return () => {
            cancelled = true;
        };
    }, [needsFetch, usecase?._id, isNer]);

    const counted = fromUsecase.length > 0 ? fromUsecase : fetched || [];
    const targets = mergeGenerationTargets(counted, catalogNames);
    const totalRecords = targets.reduce((sum, target) => sum + (target.total || 0), 0);
    return { targets, totalRecords };
}
